/**
 * A single answer button.
 *
 * State machine:
 *   - Not yet answered:    neutral border, hover-accent
 *   - Correct (revealed):  green border + tint, text-success
 *   - Selected wrong:      red border + tint, text-danger
 *   - Unselected (after reveal): neutral border, opacity-50
 *
 * `revealedAnswer` is the user's submitted answer (null until they answer);
 * `correctAnswer` is the canonical right answer. The component derives the
 * correct visual from those two.
 */

import type { ReactNode } from "react";

interface QuizOptionProps<TValue extends string | number> {
  value: TValue;
  /** Visual children (label, percent, position name, etc.). */
  children: ReactNode;
  /** The user's selection, if any (null = not yet revealed). */
  revealedAnswer: TValue | null;
  /** The canonical correct answer for this question. */
  correctAnswer: TValue;
  onSelect: (value: TValue) => void;
  /** Visual size — controls vertical padding. Defaults to `md`. */
  size?: "md" | "lg";
}

export function QuizOption<TValue extends string | number>({
  value,
  children,
  revealedAnswer,
  correctAnswer,
  onSelect,
  size = "md",
}: QuizOptionProps<TValue>) {
  const isRevealed = revealedAnswer !== null;
  const isCorrect = value === correctAnswer;
  const isSelected = value === revealedAnswer;
  const padding = size === "lg" ? "py-5" : "py-4";

  let classes = `${padding} rounded-xl border text-text font-semibold transition active:scale-[0.98]`;
  if (!isRevealed) {
    classes += " border-border bg-white/[0.02] hover:border-accent hover:bg-accent/10";
  } else if (isCorrect) {
    classes += " border-success/50 bg-success/15 text-success";
  } else if (isSelected) {
    classes += " border-danger/50 bg-danger/15 text-danger";
  } else {
    classes += " border-border bg-white/[0.02] opacity-50";
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      disabled={isRevealed}
      className={classes}
    >
      {children}
    </button>
  );
}
